import React, { createContext, useContext } from "react";
import type { BaseEntity } from "../../UniversalCRMTable/types";
import type { NavigationState } from "@/lib/types/entity-page-types";
import type { DataState, PaginationModeContextValue } from "./types";

/**
 * UniversalEntityPageDataContext
 *
 * Contains only data-related state:
 * - dataState: Current data, loading state, error state
 * - paginationModeState: Unified pagination mode state (page, pageSize, cursor, mode)
 * - navigation: Current view ID and navigation state
 *
 * Separated from other contexts to prevent unnecessary re-renders
 * when handlers or UI state changes.
 */
export interface UniversalEntityPageDataContextValue<_T extends BaseEntity = BaseEntity> {
  dataState: DataState;
  paginationModeState: PaginationModeContextValue;
  navigation: NavigationState;
}

const UniversalEntityPageDataContext = createContext<
  UniversalEntityPageDataContextValue<any> | undefined
>(undefined);

export function UniversalEntityPageDataProvider<T extends BaseEntity>({
  children,
  value,
}: {
  children: React.ReactNode;
  value: UniversalEntityPageDataContextValue<T>;
}) {
  return (
    <UniversalEntityPageDataContext.Provider value={value}>
      {children}
    </UniversalEntityPageDataContext.Provider>
  );
}

export function useUniversalEntityPageDataContext<T extends BaseEntity>() {
  const context = useContext(UniversalEntityPageDataContext);
  if (!context) {
    throw new Error(
      "useUniversalEntityPageDataContext must be used within UniversalEntityPageDataProvider"
    );
  }
  return context as UniversalEntityPageDataContextValue<T>;
}
